/**
 * CommentBadgeNode — DAG node with a speech-bubble comment badge.
 *
 * Renders the node label and overlays a small speech-bubble icon when
 * comments exist. Clicking the bubble opens the Conversations panel
 * scoped to that node.
 */

import { memo, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Handle, Position, type NodeProps } from "@xyflow/react";
import { Tooltip } from "@radix-ui/themes";
import type { DAGNode } from "../../api/artifacts";
import { nodeTooltipContent } from "./dagTooltips";
import { COMMENT_BADGE_WIDTH, COMMENT_BADGE_HEIGHT } from "./dagNodeSizing";
import { CrispNodeContent } from "./CrispNodeContent";

interface CommentBadgeNodeData {
  label: string;
  commentCount?: number;
  dagNode?: DAGNode;
  [key: string]: unknown;
}

export const CommentBadgeNode = memo(function CommentBadgeNode({ data }: NodeProps) {
  const { label, commentCount, dagNode } = data as CommentBadgeNodeData;
  const hasComments = typeof commentCount === "number" && commentCount > 0;
  const [, setSearchParams] = useSearchParams();
  const tooltipText = useMemo(() => dagNode ? nodeTooltipContent(dagNode) : label, [dagNode, label]);

  const handleBadgeClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!dagNode?.id) return;
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("nodes", dagNode.id);
        next.set("panel", "conversations");
        return next;
      }, { replace: true });
    },
    [dagNode?.id, setSearchParams],
  );

  return (
    <>
      <Handle type="target" position={Position.Top} style={{ visibility: "hidden" }} />
      <CrispNodeContent width={COMMENT_BADGE_WIDTH} height={COMMENT_BADGE_HEIGHT} style={{ overflow: "visible" }}>
      <Tooltip content={<span style={{ whiteSpace: "pre-line" }}>{tooltipText}</span>} delayDuration={400}>
      <div data-tour="comment-badge-node" style={{ position: "relative", overflow: "visible" }}>
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", display: "block", maxWidth: "100%" }}>{label}</span>
        {hasComments && (
          <span
            onClick={handleBadgeClick}
            title={`${commentCount} conversation${commentCount! > 1 ? "s" : ""}`}
            style={{
              position: "absolute",
              top: -12,
              right: -14,
              cursor: "pointer",
              lineHeight: 0,
            }}
          >
            <svg
              width="22"
              height="20"
              viewBox="0 0 22 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1 3a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-8.6L5 18.5V14H3a2 2 0 0 1-2-2V3z"
                fill="#f97316"
              />
              <text
                x="11"
                y="8.5"
                textAnchor="middle"
                dominantBaseline="central"
                fill="#fff"
                fontSize="9"
                fontWeight="700"
                fontFamily="system-ui, -apple-system, sans-serif"
              >
                {commentCount! > 9 ? "9+" : commentCount}
              </text>
            </svg>
          </span>
        )}
      </div>
      </Tooltip>
      </CrispNodeContent>
      <Handle type="source" position={Position.Bottom} style={{ visibility: "hidden" }} />
    </>
  );
});
